import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import Modal from "../actions/Modal";
import { useAuth } from "../../../context/AuthContext";
import { supabase } from "../../../lib/supabase";
import { bridgeDispatchQuestion, bridgeDispatchAnswer } from "../../../lib/bridgeDispatchSms";
import {
  QUICK_PROMPTS,
  askQuestion,
  answerQuestion,
  cancelQuestion,
  acknowledgeAnswer,
  defaultAskedTo,
  listQuestionsForDeal,
  recipientsOf,
  type DispatchQuestion,
  type DispatchPromptTemplate,
} from "./dispatchQuestionsApi";

/**
 * Dispatch Q&A modal — single component handles three modes based on context:
 *
 *  1. ASK    — dispatch staff opening on a deal with no open questions →
 *              quick-prompt buttons + custom composer + recipient picker.
 *  2. ANSWER — RM opening on a deal with an open question assigned to them →
 *              question text + answer composer.
 *  3. THREAD — anyone opening a deal with prior questions → list view +
 *              optional "Ask another" CTA.
 */

interface DealLite {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  owner_id: string | null;
  split_rm_id: string | null;
  pre_inspection_assigned_to: string | null;
  phase: string | null;
}

interface Member {
  id: string;
  full_name: string;
  role: string;
}

interface Props {
  open: boolean;
  deal: DealLite;
  onClose: () => void;
  onChanged?: () => void;
}

export default function DispatchQuestionModal({ open, deal, onClose, onChanged }: Props) {
  const { member } = useAuth();
  const [questions, setQuestions] = useState<DispatchQuestion[] | null>(null);
  const [members, setMembers] = useState<Record<string, Member>>({});
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Composer state — askedToIds is the multi-select recipient list. The first
  // ID becomes the primary; the question buzzes everyone in the list, and
  // the first reply from any of them closes it for the whole group.
  const [composing, setComposing] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<DispatchPromptTemplate | null>(null);
  const [body, setBody] = useState("");
  const [askedToIds, setAskedToIds] = useState<string[]>([]);
  // Whether the user has manually edited the recipient picks. Once true, we
  // stop auto-applying the prompt-template default so we don't blow away
  // their choice.
  const [recipientsTouched, setRecipientsTouched] = useState(false);

  // Answer state (one per open question assigned to current user)
  const [answerDraft, setAnswerDraft] = useState<Record<string, string>>({});

  const homeowner = deal.homeowner_name ?? "(no name)";

  const loadAll = async () => {
    try {
      const [qs, { data: ms }] = await Promise.all([
        listQuestionsForDeal(deal.id),
        supabase.from("team_roster").select("id,full_name,role").eq("is_active", true),
      ]);
      const memberMap: Record<string, Member> = {};
      (ms ?? []).forEach((m) => { memberMap[m.id] = m as Member; });
      setQuestions(qs);
      setMembers(memberMap);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setComposing(false);
    setSelectedPrompt(null);
    setBody("");
    setAnswerDraft({});
    setAskedToIds([]);
    setRecipientsTouched(false);
    loadAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deal.id]);

  // Auto-apply the prompt-template default recipients when the user picks
  // a quick prompt — but only if they haven't manually edited the chip
  // selection yet. This way the default is helpful but not sticky.
  useEffect(() => {
    if (!composing) return;
    if (recipientsTouched) return;
    const def = defaultAskedTo(deal, selectedPrompt);
    setAskedToIds(def);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composing, selectedPrompt]);

  const openQuestionsForMe = useMemo(
    () => (questions ?? []).filter(
      (q) => q.status === "open" && member?.id && recipientsOf(q).includes(member.id),
    ),
    [questions, member?.id],
  );

  const recipientCandidates = useMemo(() => {
    const ids = new Set<string>();
    if (deal.owner_id) ids.add(deal.owner_id);
    if (deal.split_rm_id) ids.add(deal.split_rm_id);
    if (deal.pre_inspection_assigned_to) ids.add(deal.pre_inspection_assigned_to);
    return Array.from(ids)
      .map((id) => members[id])
      .filter(Boolean) as Member[];
  }, [deal, members]);

  const pickPrompt = (key: DispatchPromptTemplate) => {
    setSelectedPrompt(key);
    if (key !== "custom") {
      const tpl = QUICK_PROMPTS.find((p) => p.key === key);
      if (tpl) setBody(tpl.bodyTemplate(homeowner));
    } else {
      setBody("");
    }
  };

  const sendAsk = async () => {
    if (!member) return;
    if (!body.trim()) return setErr("Write a question first.");
    if (askedToIds.length === 0) return setErr("Pick at least one person to ask.");
    setSubmitting(true);
    setErr(null);
    try {
      // Single question, all recipients. First reply from any of them
      // closes the question for everyone (handled server-side).
      const created = await askQuestion({
        dealId: deal.id,
        askedBy: member.id,
        askedTo: askedToIds,
        promptTemplate: selectedPrompt,
        body: body.trim(),
      });
      // Fan out the Team-line SMS — fire-and-forget; portal flow is
      // already complete and the in-portal notifications fired via trigger.
      bridgeDispatchQuestion(created.id);
      await loadAll();
      onChanged?.();
      setComposing(false);
      setSelectedPrompt(null);
      setBody("");
      setAskedToIds([]);
      setRecipientsTouched(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleRecipient = (id: string) => {
    setRecipientsTouched(true);
    setAskedToIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const sendAnswer = async (qid: string) => {
    if (!member) return;
    const draft = answerDraft[qid]?.trim();
    if (!draft) return setErr("Write an answer first.");
    setSubmitting(true);
    setErr(null);
    try {
      await answerQuestion({ questionId: qid, answeredBy: member.id, answerBody: draft });
      // Fan out the answer SMS to asker + remaining recipients.
      bridgeDispatchAnswer(qid);
      await loadAll();
      onChanged?.();
      setAnswerDraft((prev) => ({ ...prev, [qid]: "" }));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (qid: string) => {
    setSubmitting(true);
    try {
      await cancelQuestion(qid);
      await loadAll();
      onChanged?.();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const ackAnswer = async (qid: string) => {
    try {
      await acknowledgeAnswer(qid);
      await loadAll();
      onChanged?.();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  return (
    <Modal
      open={open}
      title="Dispatch Q&A"
      subtitle={`${homeowner}${deal.property_street ? ` · ${deal.property_street}` : ""}`}
      onClose={onClose}
    >
      {/* Open questions assigned to me — prioritize answer view */}
      {openQuestionsForMe.length > 0 && (
        <div className="space-y-2">
          {openQuestionsForMe.map((q) => (
            <div key={q.id} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-[11px] font-semibold text-amber-900 mb-1">
                {members[q.asked_by]?.full_name ?? "Dispatch"} asks · {format(new Date(q.created_at), "MMM d, h:mma")}
              </div>
              <div className="text-sm text-slate-800 mb-2">{q.body}</div>
              <textarea
                value={answerDraft[q.id] ?? ""}
                onChange={(e) => setAnswerDraft((prev) => ({ ...prev, [q.id]: e.target.value }))}
                rows={2}
                placeholder="Your answer…"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={() => cancel(q.id)}
                  disabled={submitting}
                  className="px-2.5 py-1 text-xs text-slate-500 hover:text-slate-700"
                >
                  Cancel question
                </button>
                <button
                  onClick={() => sendAnswer(q.id)}
                  disabled={submitting || !(answerDraft[q.id]?.trim())}
                  className="px-3 py-1 rounded bg-bardin-orange text-white text-xs font-semibold disabled:opacity-50"
                >
                  Send answer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Thread / history view */}
      {questions && questions.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">History</div>
          {questions
            .filter((q) => !openQuestionsForMe.find((o) => o.id === q.id))
            .map((q) => {
              const asker = members[q.asked_by]?.full_name ?? "Dispatch";
              const answerer = q.answered_by ? members[q.answered_by]?.full_name ?? "RM" : null;
              const recipientIds = recipientsOf(q);
              const recipientLabel = recipientIds.length === 0
                ? "RM"
                : recipientIds
                    .map((id) => members[id]?.full_name ?? "RM")
                    .join(", ");
              const isMine = q.asked_by === member?.id;
              const needsAck = isMine && q.status === "answered" && !q.acknowledged_by_asker_at;
              return (
                <div
                  key={q.id}
                  className={
                    "rounded-lg p-2.5 text-xs " +
                    (q.status === "answered"
                      ? "bg-emerald-50 border border-emerald-200"
                      : q.status === "cancelled"
                      ? "bg-slate-50 border border-slate-200"
                      : "bg-amber-50 border border-amber-200")
                  }
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-slate-700">
                      {asker} → {recipientLabel}
                    </span>
                    <span className="text-[10px] text-slate-500">
                      {format(new Date(q.created_at), "MMM d, h:mma")}
                    </span>
                  </div>
                  <div className="text-slate-800 mb-1">{q.body}</div>
                  {q.status === "answered" && (
                    <div className="text-emerald-900 bg-emerald-100/50 rounded px-2 py-1 mt-1">
                      <span className="font-semibold">{answerer}:</span> {q.answer_body}
                    </div>
                  )}
                  {q.status === "cancelled" && (
                    <div className="text-slate-500 italic">Cancelled</div>
                  )}
                  {needsAck && (
                    <div className="flex justify-end mt-1.5">
                      <button
                        onClick={() => ackAnswer(q.id)}
                        className="text-[11px] text-emerald-700 font-semibold hover:text-emerald-900"
                      >
                        ✓ Got it
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* Ask composer — collapsed state shows quick-prompt buttons */}
      {!composing ? (
        <button
          onClick={() => setComposing(true)}
          className="w-full bg-bardin-orange/10 hover:bg-bardin-orange/20 text-bardin-orange font-semibold text-sm rounded-lg py-2.5 transition"
        >
          + Ask a question
        </button>
      ) : (
        <div className="space-y-3 border-t border-slate-100 pt-3">
          <div>
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Quick prompt</div>
            <div className="flex flex-wrap gap-1.5">
              {QUICK_PROMPTS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => pickPrompt(p.key)}
                  className={
                    "px-2.5 py-1 rounded-full text-[11px] font-semibold border transition " +
                    (selectedPrompt === p.key
                      ? "bg-bardin-orange text-white border-bardin-orange"
                      : "bg-white text-slate-700 border-slate-300 hover:border-bardin-orange/50")
                  }
                >
                  {p.label}
                </button>
              ))}
              <button
                onClick={() => pickPrompt("custom")}
                className={
                  "px-2.5 py-1 rounded-full text-[11px] font-semibold border transition " +
                  (selectedPrompt === "custom"
                    ? "bg-bardin-orange text-white border-bardin-orange"
                    : "bg-white text-slate-700 border-slate-300 hover:border-bardin-orange/50")
                }
              >
                Custom…
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Ask</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={2}
              placeholder="Type your question…"
              className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">To</label>
            <div className="flex flex-wrap gap-1.5">
              {recipientCandidates.map((m) => {
                const checked = askedToIds.includes(m.id);
                const role = m.id === deal.owner_id
                  ? "Owner"
                  : m.id === deal.split_rm_id
                  ? "Split"
                  : m.id === deal.pre_inspection_assigned_to
                  ? "RS"
                  : roleLabel(m.role);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleRecipient(m.id)}
                    className={
                      "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition " +
                      (checked
                        ? "bg-bardin-orange text-white border-bardin-orange"
                        : "bg-white text-slate-700 border-slate-300 hover:border-bardin-orange/50")
                    }
                  >
                    <span className={
                      "inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm border " +
                      (checked ? "bg-white/30 border-white/60" : "bg-white border-slate-300")
                    }>
                      {checked && (
                        <svg viewBox="0 0 20 20" className="w-3 h-3 text-white" fill="currentColor"><path d="M7.629 14.571 3.357 10.3l1.286-1.286 2.986 2.985 7.428-7.428 1.286 1.286z" /></svg>
                      )}
                    </span>
                    {m.full_name}
                    <span className={checked ? "text-white/80 font-normal" : "text-slate-400 font-normal"}>· {role}</span>
                  </button>
                );
              })}
            </div>

            {/* Add anyone else */}
            <details className="mt-2 group">
              <summary className="text-[11px] text-slate-500 hover:text-slate-700 cursor-pointer select-none">+ add someone else</summary>
              <div className="mt-1.5 max-h-40 overflow-y-auto border border-slate-200 rounded p-1.5 bg-slate-50">
                <div className="flex flex-wrap gap-1">
                  {Object.values(members)
                    .filter((m) => !recipientCandidates.find((c) => c.id === m.id))
                    .sort((a, b) => a.full_name.localeCompare(b.full_name))
                    .map((m) => {
                      const checked = askedToIds.includes(m.id);
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => toggleRecipient(m.id)}
                          className={
                            "px-2 py-0.5 rounded-full text-[10px] font-semibold border transition " +
                            (checked
                              ? "bg-bardin-orange text-white border-bardin-orange"
                              : "bg-white text-slate-600 border-slate-200 hover:border-bardin-orange/40")
                          }
                        >
                          {checked ? "✓ " : ""}{m.full_name}
                          <span className={checked ? "text-white/70 font-normal ml-1" : "text-slate-400 font-normal ml-1"}>{roleLabel(m.role)}</span>
                        </button>
                      );
                    })}
                </div>
              </div>
            </details>

            {askedToIds.length > 1 && (
              <div className="text-[11px] text-slate-500 mt-1.5">
                Buzzes all {askedToIds.length}. First reply closes the question for the group.
              </div>
            )}
          </div>

          {err && <div className="bg-red-50 text-red-700 text-xs p-2 rounded">{err}</div>}

          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setComposing(false); setErr(null); }}
              disabled={submitting}
              className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800"
            >
              Cancel
            </button>
            <button
              onClick={sendAsk}
              disabled={submitting || !body.trim() || askedToIds.length === 0}
              className="px-4 py-1.5 rounded bg-bardin-orange text-white text-sm font-semibold disabled:opacity-50"
            >
              {submitting ? "Sending…" : askedToIds.length > 1 ? `Send to ${askedToIds.length}` : "Send question"}
            </button>
          </div>
        </div>
      )}

      {err && !composing && <div className="bg-red-50 text-red-700 text-xs p-2 rounded">{err}</div>}
    </Modal>
  );
}

function roleLabel(role: string): string {
  switch (role) {
    case "restore_manager": return "RM";
    case "team_lead": return "TL";
    case "asm": return "ASM";
    case "roof_team_manager": return "PM";
    case "roof_specialist": return "RS";
    case "sales_director": return "Director";
    case "director_of_operations": return "Director Ops";
    case "qc": return "QC";
    case "admin": return "Admin";
    default: return role;
  }
}
